import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { createRouter, createWebHistory } from 'vue-router';
import AppLogin from '../components/AppLogin.vue';
import ProductionForm from '../components/ProductionForm.vue';
import ProjectList from '../components/ProjectList.vue';
import UserProfile from '../components/UserProfile.vue';
import MemberManagement from '../components/MemberManagement.vue';
import DevMilestone from '../components/DevMilestone.vue';
import KnowledgeEditor from '../components/KnowledgeEditor.vue';
import KnowledgeArticleEditor from '../components/KnowledgeArticleEditor.vue';
import KnowledgeCentre from '../components/KnowledgeCentre.vue';
import OpenTickets from '../components/BugReport.vue';
import TicketsList from '../components/BugReportList.vue';
import ExternalChecklist from '@/components/ExternalChecklist.vue';
import NotFound from '@/views/NotFound.vue';
import BugReportEdit from '@/components/BugReportEdit.vue';
import ProfileDashboard from '@/components/ProfileDashboard.vue';
import ProfileAnnouncementEditor from '@/components/ProfileAnnouncementEditor.vue';

const routes = [
  {
    path: '/profile',
    name: 'UserProfile',
    component: UserProfile,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      next();
    },
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    name: 'AppLogin',
    component: AppLogin,
    // No redirection here—let the global guard handle it
  },
  {
    path: '/login',
    name: 'LoginPage',
    component: AppLogin,
    // No redirection here
  },
  {
    path: '/profile/dashboard',
    name: 'ProfileDashboard',
    component: ProfileDashboard,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        // If currentUser isn’t immediately available, wait for onAuthStateChanged.
        auth.onAuthStateChanged(async (user) => {
          if (!user) {
            return next({ name: 'AppLogin' });
          } else {
            try {
              const userDocRef = doc(db, 'users', user.uid);
              const userDocSnap = await getDoc(userDocRef);
              if (userDocSnap.exists()) {
                return next();
              } else {
                return next({ name: 'AppLogin' });
              }
            } catch (error) {
              console.error('Error fetching user data:', error);
              return next({ name: 'AppLogin' });
            }
          }
        });
      } else {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            next();
          } else {
            next({ name: 'AppLogin' });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          next({ name: 'AppLogin' });
        }
      }
    }
  },
  {
    path: '/production',
    name: 'ProductionForm',
    component: ProductionForm,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user data:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/projectList',
    name: 'ProjectList',
    component: ProjectList,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user data:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/tickets',
    name: 'TicketsList',
    component: TicketsList,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/tickets/:ticketID/details',
    name: 'TicketsDetails',
    component: BugReportEdit,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/new-ticket',
    name: 'OpenTickets',
    component: OpenTickets,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/feedback/:projectId',
    redirect: to => {
      return `/feedback/${to.params.projectId}/dashboard`
    }
  },  
  {
    path: '/feedback/:projectId/dashboard',
    name: 'ProjectFeedbackDashboard',
    component: () => import('@/components/ProjectFeedbackDashboard.vue'),
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      // Additional checks can go here...
      next();
    }
  },
  {
    path: '/announcement-editor',
    name: 'ProfileAnnouncementEditor',
    component: ProfileAnnouncementEditor,
    beforeEnter: async (to, from, next) => {
      console.log("[Route Guard] Entered /announcement-editor guard");
      await new Promise((resolve) => {
        console.log("[Route Guard] Waiting for auth state...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
          console.log("[Route Guard] Auth state changed. User:", user);
          unsubscribe();
          resolve();
        });
      });
      const user = auth.currentUser;
      console.log("[Route Guard] user after onAuthStateChanged:", user);
      if (!user) {
        console.log("[Route Guard] No user => redirecting to /login");
        return next({ name: 'AppLogin' });
      }
      try {
        console.log("[Route Guard] Checking user doc for role...");
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.log("[Route Guard] user doc doesn't exist => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        console.log("[Route Guard] user role:", role);
        if (role !== 'admin') {
          console.log("[Route Guard] user not admin => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        console.log("[Route Guard] user is admin => proceeding to /members");
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/members',
    name: 'MemberManagement',
    component: MemberManagement,
    beforeEnter: async (to, from, next) => {
      console.log("[Route Guard] Entered /members guard");
      await new Promise((resolve) => {
        console.log("[Route Guard] Waiting for auth state...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
          console.log("[Route Guard] Auth state changed. User:", user);
          unsubscribe();
          resolve();
        });
      });
      const user = auth.currentUser;
      console.log("[Route Guard] user after onAuthStateChanged:", user);
      if (!user) {
        console.log("[Route Guard] No user => redirecting to /login");
        return next({ name: 'AppLogin' });
      }
      try {
        console.log("[Route Guard] Checking user doc for role...");
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.log("[Route Guard] user doc doesn't exist => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        console.log("[Route Guard] user role:", role);
        if (role !== 'admin') {
          console.log("[Route Guard] user not admin => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        console.log("[Route Guard] user is admin => proceeding to /members");
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/knowledge-editor',
    name: 'KnowledgeEditor',
    component: KnowledgeEditor,
    beforeEnter: async (to, from, next) => {
      console.log("[Route Guard] Entered /members guard");
      await new Promise((resolve) => {
        console.log("[Route Guard] Waiting for auth state...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
          console.log("[Route Guard] Auth state changed. User:", user);
          unsubscribe();
          resolve();
        });
      });
      const user = auth.currentUser;
      console.log("[Route Guard] user after onAuthStateChanged:", user);
      if (!user) {
        console.log("[Route Guard] No user => redirecting to /login");
        return next({ name: 'AppLogin' });
      }
      try {
        console.log("[Route Guard] Checking user doc for role...");
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.log("[Route Guard] user doc doesn't exist => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        console.log("[Route Guard] user role:", role);
        if (role !== 'admin') {
          console.log("[Route Guard] user not admin => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        console.log("[Route Guard] user is admin => proceeding to /members");
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/knowledge-editor/edit/:articleId?',
    name: 'KnowledgeArticleEditor',
    component: KnowledgeArticleEditor,
    beforeEnter: async (to, from, next) => {
      console.log("[Route Guard] Entered /members guard");
      await new Promise((resolve) => {
        console.log("[Route Guard] Waiting for auth state...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
          console.log("[Route Guard] Auth state changed. User:", user);
          unsubscribe();
          resolve();
        });
      });
      const user = auth.currentUser;
      console.log("[Route Guard] user after onAuthStateChanged:", user);
      if (!user) {
        console.log("[Route Guard] No user => redirecting to /login");
        return next({ name: 'AppLogin' });
      }
      try {
        console.log("[Route Guard] Checking user doc for role...");
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.log("[Route Guard] user doc doesn't exist => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        console.log("[Route Guard] user role:", role);
        if (role !== 'admin') {
          console.log("[Route Guard] user not admin => redirecting to /login");
          return next({ name: 'AppLogin' });
        }
        console.log("[Route Guard] user is admin => proceeding to /members");
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: '/dev',
    name: 'Development',
    component: DevMilestone,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        if (role !== 'admin' && role !== 'production') {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        console.error('Error fetching user role:', error);
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    // 1) The main Knowledge Centre route
    path: '/help',
    name: 'KnowledgeCentre',
    component: KnowledgeCentre,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        // Allowed roles: admin or production
        if (role !== 'admin' && role !== 'production') {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        // Optional: log error or handle it
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    // 2) The "read article" route, restricted to admin only
    path: '/help/read/:articleId',
    name: 'KnowledgeArticle', // <--- Different name to avoid conflict
    component: KnowledgeCentre,
    beforeEnter: async (to, from, next) => {
      const user = auth.currentUser;
      if (!user) {
        return next({ name: 'AppLogin' });
      }
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          return next({ name: 'AppLogin' });
        }
        const role = userDocSnap.data().role;
        // Allowed role: admin only
        if (role !== 'admin') {
          return next({ name: 'AppLogin' });
        }
        next();
      } catch (error) {
        // Optional: log error or handle it
        next({ name: 'AppLogin' });
      }
    }
  },
  {
    path: "/external-checklist/:checkListId",
    name: "ExternalChecklist",
    component: ExternalChecklist
  },
    // Catch-all route for 404 Not Found
    { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Global navigation guard for redirecting authenticated users away from public pages.
router.beforeEach((to, from, next) => {
  const publicPages = ['AppLogin', 'LoginPage'];
  const user = auth.currentUser;
  if (publicPages.includes(to.name) && user) {
    return next({ name: 'ProfileDashboard' });
  }
  next();
});

export default router;
